/**
 * Eval Episode Tab Component
 *
 * Simplified interface for episode evaluation. Admin selects persona,
 * clicks "Run Simulation", backend runs the full conversation, results
 * are displayed at once.
 *
 * Story: 24.2 - Evolve Single Episode Eval
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useEpisode } from '../services/episodesApi';
import { useTagsFromInitialData } from 'contexts/InitialDataContext';
import {
  useFetchEpisodeForTesting,
  useRunSimulation,
  type EpisodeTestingData,
  type ToolCallRecord,
} from '../services/testingApi';
import { TestingProvider, useTesting } from '../contexts/TestingContext';
import { ConversationArea } from '../components/ConversationArea';
import { PersonaSelector } from '../components/PersonaSelector';
import { downloadTranscript } from '../utils/transcriptGenerator';

// ============================================================================
// Main Component (Wrapped with Provider)
// ============================================================================

export function EvalEpisodeTab() {
  return (
    <TestingProvider>
      <EvalEpisodeTabContent />
    </TestingProvider>
  );
}

// ============================================================================
// Content Component (Consumes Context)
// ============================================================================

function EvalEpisodeTabContent() {
  const { episodeId: episodeIdFromUrl } = useParams<{ episodeId: string }>();
  const navigate = useNavigate();

  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(undefined);
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);

  const {
    currentEpisode,
    simulationResult,
    selectedPersonaId,
    personaInstructions,
    setCurrentEpisode,
    setSimulationResult,
    setPersona,
    resetTest,
  } = useTesting();

  // API Hooks
  const { data: episodeInfo } = useEpisode(episodeIdFromUrl || undefined);
  const {
    data: episodeData,
    isLoading: isLoadingEpisode,
    error: episodeError,
    isError: isEpisodeError,
  } = useFetchEpisodeForTesting(episodeIdFromUrl || undefined, selectedLanguage);
  const tags = useTagsFromInitialData();
  const runSimulationMutation = useRunSimulation();

  // Set language from episode's languageCode
  useEffect(() => {
    if (episodeInfo && !selectedLanguage) {
      setSelectedLanguage(episodeInfo.languageCode);
    }
  }, [episodeInfo, selectedLanguage]);

  // Load episode when language is selected
  useEffect(() => {
    if (episodeData && episodeData.episodeId === episodeIdFromUrl) {
      setCurrentEpisode(episodeData);
    }
  }, [episodeData, episodeIdFromUrl, setCurrentEpisode]);

  // Clear current episode on error
  useEffect(() => {
    if (isEpisodeError && episodeIdFromUrl) {
      setCurrentEpisode(null);
    }
  }, [isEpisodeError, episodeIdFromUrl, setCurrentEpisode]);

  // Save simulation results to sessionStorage for Judge evaluation page
  useEffect(() => {
    if (episodeIdFromUrl && simulationResult) {
      sessionStorage.setItem(
        `episode_conversation_${episodeIdFromUrl}`,
        JSON.stringify(simulationResult.conversationHistory)
      );
      sessionStorage.setItem(
        `episode_tool_calls_${episodeIdFromUrl}`,
        JSON.stringify(simulationResult.toolCalls)
      );
    }
  }, [episodeIdFromUrl, simulationResult]);

  // ========== Handlers ==========

  const handleRunSimulation = async () => {
    if (!currentEpisode) {
      toast.error('Please select an episode first');
      return;
    }
    if (!personaInstructions) {
      toast.error('Please select a persona first');
      return;
    }

    resetTest();

    try {
      const result = await runSimulationMutation.mutateAsync({
        episodeId: currentEpisode.episodeId,
        language: currentEpisode.language,
        personaInstructions,
        maxTurns: 50,
      });

      setSimulationResult(result);
      toast.success(`Simulation complete (${result.completionReason})`);
    } catch {
      toast.error('Simulation failed. Please try again.');
    }
  };

  const handleEvaluateWithJudge = () => {
    if (!currentEpisode || !simulationResult) {
      toast.error('No simulation results to evaluate');
      return;
    }

    navigate(
      `/episodes/eval/${currentEpisode.episodeId}/judge?language=${currentEpisode.language}`
    );
  };

  const handleDownloadTranscript = useCallback(() => {
    if (!currentEpisode || !simulationResult) return;
    downloadTranscript({
      episode: currentEpisode,
      conversationHistory: simulationResult.conversationHistory,
      toolCalls: simulationResult.toolCalls,
      completionReason: simulationResult.completionReason,
      exchangeCount: simulationResult.exchangeCount,
    });
  }, [currentEpisode, simulationResult]);

  const handleReset = () => {
    resetTest();
    toast.success('Results cleared');
  };

  // ========== Render ==========

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Episode Evaluation</h1>
        <Link to="/episodes/eval" className="text-blue-600 hover:text-blue-800 font-medium">
          &larr; Back to Episode Selector
        </Link>
      </div>

      {/* Missing Episode ID Error */}
      {!episodeIdFromUrl && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">No Episode Selected</h3>
          <p className="text-sm text-yellow-800 mb-4">
            Please select an episode from the Episode Selector to begin evaluation.
          </p>
          <Link
            to="/episodes/eval"
            className="text-sm font-medium text-yellow-700 hover:text-yellow-900 underline"
          >
            &larr; Go to Episode Selector
          </Link>
        </div>
      )}

      {/* Language Display */}
      {episodeIdFromUrl && episodeInfo && (
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Language:</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {episodeInfo.languageCode.toUpperCase()}
              </span>
            </div>
            {currentEpisode && !isLoadingEpisode && !isEpisodeError && (
              <button
                onClick={() => setIsContextModalOpen(true)}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 font-medium flex items-center gap-2 border border-blue-200 rounded-md transition-colors"
                title="View Details"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                  />
                </svg>
                Details
              </button>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoadingEpisode && selectedLanguage && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      )}

      {/* Error State */}
      {isEpisodeError && episodeIdFromUrl && selectedLanguage && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Episode</h3>
          <p className="text-sm text-red-800 mb-4">
            {episodeError instanceof Error
              ? episodeError.message
              : 'An error occurred while loading the episode for evaluation.'}
          </p>
          <Link
            to="/episodes/eval"
            className="text-sm font-medium text-red-700 hover:text-red-900 underline"
          >
            &larr; Go back to Episode Selector
          </Link>
        </div>
      )}

      {/* Episode Details Modal */}
      {currentEpisode && !isLoadingEpisode && !isEpisodeError && (
        <EpisodeContextModal
          isOpen={isContextModalOpen}
          onClose={() => setIsContextModalOpen(false)}
          episode={currentEpisode}
          tags={tags}
        />
      )}

      {/* Main Content Area */}
      {currentEpisode && !isLoadingEpisode && !isEpisodeError && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Context + Persona Selector */}
          <div className="sticky top-4 self-start space-y-4">
            <EpisodeContextCard context={currentEpisode.context} />

            {/* Persona Selector */}
            <div className="bg-white border border-gray-300 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Simulated User Persona</h3>
              <PersonaSelector
                episodeComplexityLevel={currentEpisode.complexityLevel}
                selectedPersonaId={selectedPersonaId}
                onPersonaSelect={(persona) => setPersona(persona.id, persona.description)}
              />
            </div>

            {/* Run Simulation Button */}
            <button
              onClick={handleRunSimulation}
              disabled={!selectedPersonaId || runSimulationMutation.isPending}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-lg font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {runSimulationMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Running Simulation...
                </span>
              ) : (
                'Run Simulation'
              )}
            </button>

            {/* Reset Button */}
            {simulationResult && (
              <button
                onClick={handleReset}
                className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors font-medium"
              >
                Clear Results
              </button>
            )}
          </div>

          {/* Middle Column: Conversation Transcript */}
          <div className="space-y-4">
            {/* Simulation Info Bar */}
            {simulationResult && (
              <div className="flex items-center justify-between bg-white border border-gray-300 rounded-lg px-4 py-2">
                <div className="flex items-center gap-4 text-sm text-gray-700">
                  <span>
                    Exchanges: <strong>{simulationResult.exchangeCount}</strong>
                  </span>
                  <span>
                    Reason:{' '}
                    <strong className="capitalize">
                      {simulationResult.completionReason.replace('_', ' ')}
                    </strong>
                  </span>
                </div>
                <button
                  onClick={handleDownloadTranscript}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-300 rounded-md transition-colors flex items-center gap-1.5"
                  title="Download transcript as Markdown"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                    />
                  </svg>
                  Download Transcript
                </button>
              </div>
            )}

            {/* Conversation Area */}
            <div className="h-[600px]">
              {simulationResult ? (
                <ConversationArea messages={simulationResult.conversationHistory} />
              ) : (
                <div className="h-full flex items-center justify-center bg-white border border-gray-300 rounded-lg">
                  <p className="text-gray-400 text-lg">
                    {runSimulationMutation.isPending
                      ? 'Running simulation...'
                      : 'Select a persona and run simulation to see results'}
                  </p>
                </div>
              )}
            </div>

            {/* Tool Call Log */}
            {simulationResult && <ToolCallLogSection toolCalls={simulationResult.toolCalls} />}
          </div>

          {/* Right Column: Goals + Personas + Evaluate Button */}
          <div className="space-y-4 sticky top-4 self-start">
            <GoalsCard goals={currentEpisode.goals} />
            <PersonasCard personas={currentEpisode.personas} />

            {/* Evaluate with Judge Button */}
            {simulationResult && (
              <div className="bg-white border border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Evaluation</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Simulation complete! Evaluate the conversation quality with the Judge agent.
                </p>
                <button
                  onClick={handleEvaluateWithJudge}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Evaluate with Judge
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tool Call Log Section Component
// ============================================================================

interface ToolCallLogSectionProps {
  toolCalls: ToolCallRecord[];
}

function ToolCallLogSection({ toolCalls }: ToolCallLogSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white border border-gray-300 rounded-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <span className="text-sm font-semibold text-gray-800">Tool Calls ({toolCalls.length})</span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {isExpanded && (
        <div className="px-4 pb-4">
          {toolCalls.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No tool calls recorded</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Turn</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Tool</th>
                  <th className="text-left py-2 font-medium text-gray-600">Arguments</th>
                </tr>
              </thead>
              <tbody>
                {toolCalls.map((tc, index) => (
                  <tr key={index} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4 text-gray-700">{tc.afterTurn}</td>
                    <td className="py-2 pr-4 font-mono text-gray-900">{tc.toolName}</td>
                    <td className="py-2 font-mono text-gray-700 text-xs">
                      {tc.arguments ? JSON.stringify(tc.arguments) : '{}'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Episode Details Modal Component
// ============================================================================

interface EpisodeContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  episode: EpisodeTestingData;
  tags?: Array<{ id: number; name: string }>;
}

function EpisodeContextModal({ isOpen, onClose, episode, tags }: EpisodeContextModalProps) {
  const episodeTags = tags
    ? episode.tagIds.map((tagId) => tags.find((tag) => tag.id === tagId)?.name).filter(Boolean)
    : [];

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Episode Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Name
            </h3>
            <p className="text-gray-900">{episode.name}</p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
                Complexity
              </h3>
              <p className="text-gray-900">{episode.complexityLevel}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
                Duration
              </h3>
              <p className="text-gray-900">
                {episode.durationId === 1
                  ? 'Short'
                  : episode.durationId === 2
                    ? 'Medium'
                    : episode.durationId === 3
                      ? 'Long'
                      : 'Extended'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
                Language
              </h3>
              <p className="text-gray-900">{episode.language.toUpperCase()}</p>
            </div>
          </div>

          {episodeTags.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {episodeTags.map((tagName, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                  >
                    {tagName}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Context
            </h3>
            <p className="text-gray-900 whitespace-pre-wrap">{episode.context}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Scenario
            </h3>
            <p className="text-gray-900 whitespace-pre-wrap">{episode.scenario}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Goals
            </h3>
            <ul className="list-disc list-inside space-y-1">
              {episode.goals.map((goal, index) => (
                <li key={index} className="text-gray-900">
                  {goal}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Personas
            </h3>
            <div className="space-y-2">
              {episode.personas.map((persona, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3">
                  <p className="font-semibold text-gray-900">{persona.name}</p>
                  <p className="text-sm text-gray-700">{persona.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Context Card Component
// ============================================================================

function EpisodeContextCard({ context }: { context: string }) {
  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Context</h3>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{context}</p>
    </div>
  );
}

// ============================================================================
// Personas Card Component
// ============================================================================

function PersonasCard({ personas }: { personas: Array<{ name: string; description: string }> }) {
  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Personas</h3>
      <div className="space-y-3">
        {personas.map((persona, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-3">
            <p className="font-semibold text-gray-900 text-sm mb-1">{persona.name}</p>
            <p className="text-xs text-gray-700">{persona.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Goals Card Component
// ============================================================================

function GoalsCard({ goals }: { goals: string[] }) {
  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Goals</h3>
      <ol className="space-y-2 list-decimal list-inside">
        {goals.map((goal, index) => (
          <li key={index} className="text-sm text-gray-700">
            {goal}
          </li>
        ))}
      </ol>
    </div>
  );
}
